import './App.css';
import { useState, useEffect, useRef } from 'react';
import { SignIn, SignUp, SignedIn, SignedOut, UserButton, useUser, useAuth } from '@clerk/clerk-react';

const ZapIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const CodeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

const BriefcaseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
  </svg>
);

const BookIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);

function ModeIcon({ mode }) {
  const icons = { skill: ZapIcon, project: CodeIcon, job: BriefcaseIcon, topic: BookIcon };
  const Icon = icons[mode];
  return Icon ? <Icon /> : null;
}

const MODES = {
  skill: {
    label: 'Skill Interview',
    Icon: ZapIcon,
    desc: 'Practice a specific technical skill with adaptive difficulty',
    topicPlaceholder: 'e.g. Python, React, SQL, Machine Learning...',
    contextLabel: null,
    chips: ['Python', 'System Design', 'SQL', 'Machine Learning', 'Data Structures'],
  },
  project: {
    label: 'Project Interview',
    Icon: CodeIcon,
    desc: 'Get interviewed about a project you built',
    topicPlaceholder: 'Project name (e.g. E-commerce app, Chat bot)',
    contextLabel: 'Brief description — tech stack, what it does, your role (optional)',
    chips: null,
  },
  job: {
    label: 'Job Interview',
    Icon: BriefcaseIcon,
    desc: 'Prepare for a specific job or role',
    topicPlaceholder: 'Job title (e.g. Software Engineer, Data Analyst)',
    contextLabel: 'Company or key requirements (optional)',
    chips: ['Software Engineer', 'Data Analyst', 'Product Manager', 'Frontend Developer'],
  },
  topic: {
    label: 'Topic Interview',
    Icon: BookIcon,
    desc: 'Explore any subject or field',
    topicPlaceholder: 'e.g. data structures, organic chemistry, constitutional law...',
    contextLabel: null,
    chips: ['Data Structures', 'Machine Learning', 'System Design', 'Algorithms'],
  },
};

function App() {
  const { getToken } = useAuth();
  const { user } = useUser();

  const [authMode, setAuthMode] = useState('signin');
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [screen, setScreen] = useState('home');
  const [selectedMode, setSelectedMode] = useState('skill');
  const [topic, setTopic] = useState('');
  const [context, setContext] = useState('');
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');
  const [progress, setProgress] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const chatEndRef = useRef(null);

  const API = 'https://prepfully-api.onrender.com';
  const activeSession = sessions.find(s => s.id === activeSessionId);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeSession?.messages?.length, loading]);

  useEffect(() => {
    if (user) {
      apiGet(API + '/sessions').then(data => {
        if (Array.isArray(data)) setSessions(data);
      }).catch(() => {});
    }
  }, [user?.id]); // eslint-disable-line

  async function saveSession(session) {
    try {
      const token = await getToken();
      await fetch(API + '/sessions', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify(session)
      });
    } catch (e) {}
  }

  async function apiPost(url) {
    const token = await getToken();
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + token }
    });
    return res.json();
  }

  async function apiGet(url) {
    const token = await getToken();
    const res = await fetch(url, {
      headers: { 'Authorization': 'Bearer ' + token }
    });
    return res.json();
  }

  async function loadProgress() {
    const data = await apiGet(API + '/progress');
    setProgress(data);
  }

  async function deleteProgress(topicName) {
    const token = await getToken();
    await fetch(API + '/progress?' + new URLSearchParams({ topic: topicName }), {
      method: 'DELETE',
      headers: { 'Authorization': 'Bearer ' + token }
    });
    setProgress(prev => {
      const updated = { ...prev };
      delete updated[topicName];
      return updated;
    });
  }

  function updateSession(sessionId, updater) {
    let result;
    setSessions(prev => prev.map(s => {
      if (s.id !== sessionId) return s;
      result = updater(s);
      return result;
    }));
    return result;
  }

  async function startNewSession() {
    if (!topic.trim()) return;
    setLoading(true);
    setLoadingText('Crafting your first question...');

    try {
      const params = new URLSearchParams({ topic, mode: selectedMode, context });
      const data = await apiPost(API + '/generate-question?' + params);
      const modeLabel = MODES[selectedMode].label;
      const newSession = {
        id: Date.now().toString(),
        topic: topic,
        mode: selectedMode,
        context: context,
        difficulty: data.difficulty,
        messages: [
          { type: 'system', content: modeLabel + ' started — ' + topic + ' (' + data.difficulty + ')' },
          { type: 'question', content: data.question }
        ]
      };
      setSessions(prev => [newSession, ...prev]);
      setActiveSessionId(newSession.id);
      setScreen('chat');
      setTopic('');
      setContext('');
      saveSession(newSession);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  async function submitAnswer() {
    if (!answer.trim() || !activeSession) return;
    setLoading(true);
    setLoadingText('Evaluating your answer...');

    const currentQuestion = [...activeSession.messages].reverse().find(m => m.type === 'question')?.content;

    updateSession(activeSessionId, s => ({
      ...s,
      messages: [...s.messages, { type: 'answer', content: answer }]
    }));

    const userAnswer = answer;
    setAnswer('');

    try {
      const params = new URLSearchParams({
        topic: activeSession.topic,
        question: currentQuestion,
        answer: userAnswer,
        mode: activeSession.mode || 'skill',
        context: activeSession.context || ''
      });
      const data = await apiPost(API + '/evaluate-answer?' + params);

      const afterFeedback = updateSession(activeSessionId, s => ({
        ...s,
        difficulty: data.next_difficulty || s.difficulty,
        messages: [...s.messages, { type: 'feedback', content: data }]
      }));
      if (afterFeedback) saveSession(afterFeedback);
    } catch (e) {
      console.error(e);
      updateSession(activeSessionId, s => ({
        ...s,
        messages: [...s.messages, { type: 'system', content: 'Error evaluating answer. Try again.' }]
      }));
    }
    setLoading(false);
  }

  async function nextQuestion() {
    if (!activeSession) return;
    setLoading(true);
    setLoadingText('Getting your next question...');

    try {
      const params = new URLSearchParams({
        topic: activeSession.topic,
        mode: activeSession.mode || 'skill',
        context: activeSession.context || ''
      });
      const data = await apiPost(API + '/generate-question?' + params);
      const afterNext = updateSession(activeSessionId, s => ({
        ...s,
        difficulty: data.difficulty,
        messages: [...s.messages,
          { type: 'system', content: 'Next question — ' + data.difficulty + ' difficulty' },
          { type: 'question', content: data.question }
        ]
      }));
      if (afterNext) saveSession(afterNext);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  async function skipQuestion() {
    if (!activeSession) return;
    setLoading(true);
    setLoadingText('Skipping...');

    const currentQuestion = [...activeSession.messages].reverse().find(m => m.type === 'question')?.content;

    try {
      const skipParams = new URLSearchParams({ topic: activeSession.topic, question: currentQuestion });
      await apiPost(API + '/skip-question?' + skipParams);

      const nextParams = new URLSearchParams({
        topic: activeSession.topic,
        mode: activeSession.mode || 'skill',
        context: activeSession.context || ''
      });
      const data = await apiPost(API + '/generate-question?' + nextParams);

      const afterSkip = updateSession(activeSessionId, s => ({
        ...s,
        difficulty: data.difficulty,
        messages: [...s.messages,
          { type: 'system', content: 'Question skipped' },
          { type: 'question', content: data.question }
        ]
      }));
      if (afterSkip) saveSession(afterSkip);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  useEffect(() => {
    if (!openMenuId) return;
    const close = () => setOpenMenuId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [openMenuId]);

  async function deleteSession(sessionId) {
    const session = sessions.find(s => s.id === sessionId);
    setSessions(prev => prev.filter(s => s.id !== sessionId));
    if (activeSessionId === sessionId) {
      setActiveSessionId(null);
      setScreen('home');
    }
    if (session) {
      try {
        const token = await getToken();
        await fetch(API + '/sessions/' + session.id, {
          method: 'DELETE',
          headers: { 'Authorization': 'Bearer ' + token }
        });
        await fetch(API + '/progress?' + new URLSearchParams({ topic: session.topic }), {
          method: 'DELETE',
          headers: { 'Authorization': 'Bearer ' + token }
        });
      } catch (e) {}
    }
  }

  function getDifficultyColor(diff) {
    if (diff === 'beginner') return '#34d399';
    if (diff === 'intermediate') return '#fbbf24';
    if (diff === 'advanced') return '#f87171';
    return '#94a3b8';
  }

  function getScoreColor(score) {
    if (score >= 4) return '#34d399';
    if (score === 3) return '#fbbf24';
    return '#f87171';
  }

  function getScoreLabel(score) {
    if (score >= 4) return 'Strong answer';
    if (score === 3) return 'Decent attempt';
    return 'Needs work';
  }

  const lastMessage = activeSession?.messages?.[activeSession.messages.length - 1];
  const waitingForAnswer = lastMessage?.type === 'question';
  const waitingForNext = lastMessage?.type === 'feedback';

  // Auth screen
  if (!user) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-brand">
            <div className="auth-logo">P</div>
            <h1>Prepfully</h1>
            <p>AI-powered interview prep for any field</p>
          </div>
          <div className="auth-tabs">
            <button
              className={'auth-tab' + (authMode === 'signin' ? ' active' : '')}
              onClick={() => setAuthMode('signin')}
            >
              Sign In
            </button>
            <button
              className={'auth-tab' + (authMode === 'signup' ? ' active' : '')}
              onClick={() => setAuthMode('signup')}
            >
              Register
            </button>
          </div>
          <SignedOut>
            {authMode === 'signin' ? <SignIn /> : <SignUp />}
          </SignedOut>
        </div>
        <div className="auth-bg">
          <div className="auth-grid"></div>
        </div>
      </div>
    );
  }

  return (
    <SignedIn>
      <div className="app-layout">
        <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-header">
            <div className="sidebar-brand">
              <span className="sidebar-logo">P</span>
              {sidebarOpen && <span className="sidebar-title">Prepfully</span>}
            </div>
            <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
              {sidebarOpen ? '‹' : '›'}
            </button>
          </div>

          {sidebarOpen && (
            <div className="sidebar-actions">
              <button className="btn-new-chat" onClick={() => { setScreen('home'); setActiveSessionId(null); }}>
                + New Interview
              </button>
            </div>
          )}

          <nav className="sidebar-nav">
            {sidebarOpen ? (
              <>
                <div className="nav-section-label">Sessions</div>
                {sessions.length === 0 && (
                  <p className="nav-empty">No sessions yet</p>
                )}
                {sessions.map(session => (
                  <div
                    key={session.id}
                    className={`session-item ${activeSessionId === session.id ? 'active' : ''}`}
                    onClick={() => { setActiveSessionId(session.id); setScreen('chat'); }}
                  >
                    <div className="session-info">
                      <span className="session-topic">
                        <ModeIcon mode={session.mode} /> {session.topic}
                      </span>
                      <span className="session-meta">
                        <span style={{ color: getDifficultyColor(session.difficulty) }}>●</span>{' '}
                        {session.messages.filter(m => m.type === 'question').length} questions
                      </span>
                    </div>
                    <div className="session-menu-wrap" onClick={e => e.stopPropagation()}>
                      <button className="session-menu-btn" onClick={() => setOpenMenuId(openMenuId === session.id ? null : session.id)}>
                        <svg viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/></svg>
                      </button>
                      {openMenuId === session.id && (
                        <div className="session-dropdown">
                          <button className="session-dropdown-delete" onClick={() => { deleteSession(session.id); setOpenMenuId(null); }}>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                <button
                  className={`nav-item ${screen === 'progress' ? 'active' : ''}`}
                  onClick={() => { setScreen('progress'); loadProgress(); }}
                >
                  <span className="nav-icon">📊</span>
                  <span>Progress</span>
                </button>
              </>
            ) : (
              <>
                <button className="nav-item" onClick={() => { setScreen('home'); setActiveSessionId(null); }} title="New interview">
                  <span className="nav-icon">+</span>
                </button>
                {sessions.map(session => (
                  <button
                    key={session.id}
                    className={`nav-item ${activeSessionId === session.id ? 'active' : ''}`}
                    onClick={() => { setActiveSessionId(session.id); setScreen('chat'); }}
                    title={session.topic}
                  >
                    <span className="nav-icon"><ModeIcon mode={session.mode} /></span>
                  </button>
                ))}
                <button
                  className={`nav-item ${screen === 'progress' ? 'active' : ''}`}
                  onClick={() => { setScreen('progress'); loadProgress(); }}
                  title="Progress"
                >
                  <span className="nav-icon">📊</span>
                </button>
              </>
            )}
          </nav>

          <div className="sidebar-footer">
            <div className="sidebar-user">
              <img src={user?.imageUrl} className="user-avatar" alt="" />
              {sidebarOpen && <span className="user-name">{user?.firstName || 'User'}</span>}
              <div className="user-button-overlay">
                <UserButton />
              </div>
            </div>
          </div>
        </aside>

        <main className="main-content">
          {screen === 'home' && (
            <div className="screen home-screen">
              <div className="home-hero">
                <h1>How do you want to <span className="gradient-text">prepare</span>?</h1>
                <p className="home-subtitle">Pick a mode and start your AI-powered mock interview.</p>
              </div>

              {/* Mode Selection */}
              <div className="mode-grid">
                {Object.entries(MODES).map(([id, m]) => (
                  <button
                    key={id}
                    className={`mode-card ${selectedMode === id ? 'selected' : ''}`}
                    onClick={() => { setSelectedMode(id); setTopic(''); setContext(''); }}
                  >
                    <div className="mode-icon"><m.Icon /></div>
                    <div className="mode-label">{m.label}</div>
                    <div className="mode-desc">{m.desc}</div>
                  </button>
                ))}
              </div>

              {/* Input area */}
              <div className="input-section">
                <input
                  type="text"
                  className="topic-input"
                  placeholder={MODES[selectedMode].topicPlaceholder}
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !MODES[selectedMode].contextLabel && startNewSession()}
                />

                {MODES[selectedMode].contextLabel && (
                  <textarea
                    className="context-textarea"
                    placeholder={MODES[selectedMode].contextLabel}
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    rows={3}
                  />
                )}

                <button className="btn-primary btn-start" onClick={startNewSession} disabled={!topic.trim() || loading}>
                  {loading ? loadingText : 'Start ' + MODES[selectedMode].label + ' →'}
                </button>

                {MODES[selectedMode].chips && (
                  <div className="quick-topics">
                    <span className="quick-label">Quick start:</span>
                    {MODES[selectedMode].chips.map(t => (
                      <button key={t} className="chip" onClick={() => setTopic(t)}>{t}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {screen === 'chat' && activeSession && (
            <div className="screen chat-screen">
              <div className="chat-header">
                <div className="interview-meta">
                  <span className="meta-mode-badge"><ModeIcon mode={activeSession.mode} /> {MODES[activeSession.mode]?.label}</span>
                  <span className="meta-topic">{activeSession.topic}</span>
                  <span className="meta-difficulty" style={{ color: getDifficultyColor(activeSession.difficulty) }}>
                    ● {activeSession.difficulty}
                  </span>
                </div>
              </div>

              <div className="chat-messages">
                {activeSession.messages.map((msg, i) => (
                  <div key={i} className={'chat-bubble ' + msg.type}>
                    {msg.type === 'system' && (
                      <div className="bubble-system">{msg.content}</div>
                    )}
                    {msg.type === 'question' && (
                      <div className="bubble-question">
                        <div className="bubble-label">Question</div>
                        <p>{msg.content}</p>
                      </div>
                    )}
                    {msg.type === 'answer' && (
                      <div className="bubble-answer">
                        <div className="bubble-label">Your answer</div>
                        <p>{msg.content}</p>
                      </div>
                    )}
                    {msg.type === 'feedback' && (
                      <div className="bubble-feedback">
                        <div className="feedback-score-row">
                          <div className="mini-score" style={{ borderColor: getScoreColor(msg.content.score), color: getScoreColor(msg.content.score) }}>
                            {msg.content.score}/5
                          </div>
                          <span className="score-verdict">{getScoreLabel(msg.content.score)}</span>
                        </div>
                        <div className="bubble-section">
                          <div className="bubble-label">Feedback</div>
                          <p>{msg.content.feedback}</p>
                        </div>
                        <div className="bubble-section">
                          <div className="bubble-label">Ideal answer</div>
                          <p className="ideal-text">{msg.content.ideal}</p>
                        </div>
                        {msg.content.next_difficulty && (
                          <div className="next-diff">
                            Next: <span style={{ color: getDifficultyColor(msg.content.next_difficulty) }}>{msg.content.next_difficulty}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {loading && (
                  <div className="chat-bubble system">
                    <div className="bubble-system loading-bubble">
                      <div className="typing-dots"><span></span><span></span><span></span></div>
                      {loadingText}
                    </div>
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>

              <div className="chat-input-area">
                {waitingForAnswer && !loading && (
                  <>
                    <textarea
                      className="chat-textarea"
                      placeholder="Type your answer... (Enter to submit, Shift+Enter for new line)"
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          submitAnswer();
                        }
                      }}
                      rows={3}
                    />
                    <div className="chat-actions">
                      <button className="btn-primary" onClick={submitAnswer} disabled={!answer.trim()}>Submit</button>
                      <button className="btn-secondary btn-sm" onClick={skipQuestion}>Skip</button>
                    </div>
                  </>
                )}
                {waitingForNext && !loading && (
                  <div className="chat-actions center">
                    <button className="btn-primary" onClick={nextQuestion}>Next Question →</button>
                    <button className="btn-secondary" onClick={() => { setScreen('home'); setActiveSessionId(null); }}>New Interview</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {screen === 'progress' && (
            <div className="screen progress-screen">
              <h1>Your Progress</h1>
              <p className="home-subtitle">Track your improvement across topics.</p>

              {progress && Object.keys(progress).length > 0 && !progress.error ? (
                <div className="progress-grid">
                  {Object.entries(progress).map(([topicName, data]) => (
                    <div className="progress-card" key={topicName}>
                      <div className="progress-card-header">
                        <h3>{topicName}</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="meta-difficulty" style={{ color: getDifficultyColor(data.difficulty) }}>
                            ● {data.difficulty}
                          </span>
                          <button className="progress-delete-btn" onClick={() => deleteProgress(topicName)} title="Remove">×</button>
                        </div>
                      </div>
                      <div className="progress-stats">
                        <div className="stat">
                          <span className="stat-num">{data.total_questions}</span>
                          <span className="stat-label">Questions</span>
                        </div>
                        <div className="stat">
                          <span className="stat-num" style={{ color: getScoreColor(Math.round(data.average_score)) }}>
                            {data.average_score}
                          </span>
                          <span className="stat-label">Avg Score</span>
                        </div>
                      </div>
                      <button className="btn-secondary" style={{ width: '100%', marginTop: '12px' }} onClick={() => { setTopic(topicName); setScreen('home'); }}>
                        Practice again
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No progress yet. Start practicing to see your stats here!</p>
                  <button className="btn-primary" onClick={() => setScreen('home')}>Start Practicing</button>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </SignedIn>
  );
}

export default App;
